import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Save, UserPlus } from 'lucide-react';
import api from '../services/api';
import './UserCreate.css';

const ROLE_KEYS = ['admin', 'director', 'deputy_director', 'department_head', 'employee', 'analyst'];

function UserCreate() {
  const { t } = useTranslation(['admin', 'common']);
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [departments, setDepartments] = useState([]);

  const [form, setForm] = useState({
    username: '',
    first_name: '',
    last_name: '',
    middle_name: '',
    email: '',
    phone: '',
    role: 'employee',
    department: '',
    position: '',
    password: '',
    hire_date: '',
    birth_date: '',
    is_active: true,
    is_staff: false,
  });

  useEffect(() => {
    fetchDepartments();
    if (isEdit) fetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchDepartments = async () => {
    try {
      const res = await api.getDepartments();
      setDepartments(res.data.results || res.data);
    } catch (err) {
      console.error('Error loading departments:', err);
    }
  };

  const fetchUser = async () => {
    try {
      setLoading(true);
      const res = await api.getUser(id);
      const u = res.data;
      setForm({
        username: u.username || '',
        first_name: u.first_name || '',
        last_name: u.last_name || '',
        middle_name: u.middle_name || '',
        email: u.email || '',
        phone: u.phone || '',
        role: u.role || 'employee',
        department: u.department ? String(u.department) : '',
        position: u.position || '',
        password: '',
        hire_date: u.hire_date || '',
        birth_date: u.birth_date || '',
        is_active: u.is_active !== false,
        is_staff: u.is_staff || false,
      });
      setLoading(false);
    } catch (err) {
      console.error('Error loading user:', err);
      alert(t('admin:users.loadError'));
      navigate('/users');
    }
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.username || !form.role) {
      alert(t('admin:users.requiredFields'));
      return;
    }

    if (!isEdit && !form.password) {
      alert(t('admin:users.passwordRequired'));
      return;
    }

    setSaving(true);
    try {
      const data = {
        username: form.username,
        first_name: form.first_name,
        last_name: form.last_name,
        middle_name: form.middle_name,
        email: form.email,
        phone: form.phone,
        role: form.role,
        department: form.department ? parseInt(form.department) : null,
        position: form.position,
        hire_date: form.hire_date || null,
        birth_date: form.birth_date || null,
        is_active: form.is_active,
        is_staff: form.is_staff,
      };

      if (form.password) {
        data.password = form.password;
      }

      if (isEdit) {
        await api.updateUser(id, data);
        alert(t('admin:users.updated'));
      } else {
        await api.createUser(data);
        alert(t('admin:users.created'));
      }

      navigate('/users');
    } catch (err) {
      console.error('Error saving:', err);
      const detail = err.response?.data;
      if (typeof detail === 'object') {
        const msg = Object.entries(detail).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join('\n');
        alert(t('admin:users.saveError') + ':\n' + msg);
      } else {
        alert(t('admin:users.saveError') + ': ' + (detail || err.message));
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="user-create-page"><div className="loading-spinner">{t('common:table.loading')}</div></div>;
  }

  return (
    <div className="user-create-page">
      <div className="user-create-header">
        <h1>
          <UserPlus size={24} />
          {isEdit ? t('admin:users.editUser') : t('admin:users.createUser')}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="user-form">
        <div className="form-card">
          <h2>{t('admin:users.accountSection')}</h2>

          <div className="form-row">
            <div className="form-group">
              <label>{t('admin:users.login')} *</label>
              <input
                type="text"
                className="form-control"
                value={form.username}
                onChange={(e) => handleChange('username', e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label>{isEdit ? t('admin:users.passwordEdit') : t('admin:users.password') + ' *'}</label>
              <input
                type="password"
                className="form-control"
                value={form.password}
                onChange={(e) => handleChange('password', e.target.value)}
                required={!isEdit}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>{t('admin:users.role')} *</label>
              <select
                className="form-control"
                value={form.role}
                onChange={(e) => handleChange('role', e.target.value)}
              >
                {ROLE_KEYS.map(key => (
                  <option key={key} value={key}>{t(`common:roles.${key}`)}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>{t('admin:users.department')}</label>
              <select
                className="form-control"
                value={form.department}
                onChange={(e) => handleChange('department', e.target.value)}
              >
                <option value="">{t('admin:users.noDepartment')}</option>
                {departments.map(d => (
                  <option key={d.id} value={d.id}>{d.name_ru || d.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="form-card">
          <h2>{t('admin:users.personalSection')}</h2>

          <div className="form-row form-row-3">
            <div className="form-group">
              <label>{t('admin:users.lastName')}</label>
              <input type="text" className="form-control" value={form.last_name} onChange={(e) => handleChange('last_name', e.target.value)} />
            </div>
            <div className="form-group">
              <label>{t('admin:users.firstName')}</label>
              <input type="text" className="form-control" value={form.first_name} onChange={(e) => handleChange('first_name', e.target.value)} />
            </div>
            <div className="form-group">
              <label>{t('admin:users.middleName')}</label>
              <input type="text" className="form-control" value={form.middle_name} onChange={(e) => handleChange('middle_name', e.target.value)} />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>{t('admin:users.email')}</label>
              <input type="email" className="form-control" value={form.email} onChange={(e) => handleChange('email', e.target.value)} />
            </div>
            <div className="form-group">
              <label>{t('admin:users.phone')}</label>
              <input type="tel" className="form-control" value={form.phone} onChange={(e) => handleChange('phone', e.target.value)} />
            </div>
          </div>

          <div className="form-group">
            <label>{t('admin:users.position')}</label>
            <input type="text" className="form-control" value={form.position} onChange={(e) => handleChange('position', e.target.value)} />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Дата приёма на работу</label>
              <input type="date" className="form-control" value={form.hire_date} onChange={(e) => handleChange('hire_date', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Дата рождения</label>
              <input type="date" className="form-control" value={form.birth_date} onChange={(e) => handleChange('birth_date', e.target.value)} />
            </div>
          </div>
        </div>

        <div className="form-card">
          <h2>{t('admin:users.accessSection')}</h2>
          <div className="checkbox-row">
            <label className="checkbox-label">
              <input type="checkbox" checked={form.is_active} onChange={(e) => handleChange('is_active', e.target.checked)} />
              <span>{t('admin:users.activeAccount')}</span>
            </label>
            <label className="checkbox-label">
              <input type="checkbox" checked={form.is_staff} onChange={(e) => handleChange('is_staff', e.target.checked)} />
              <span>{t('admin:users.staffAccess')}</span>
            </label>
          </div>
        </div>

        <div className="form-actions">
          <button type="button" className="btn-secondary" onClick={() => navigate('/users')}>{t('common:buttons.cancel')}</button>
          <button type="submit" className="btn-primary" disabled={saving}>
            <Save size={18} />
            {saving ? t('admin:settings.saving') : (isEdit ? t('common:buttons.save') : t('common:buttons.create'))}
          </button>
        </div>
      </form>
    </div>
  );
}

export default UserCreate;
